import React, { useEffect, useRef, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import Swal from "sweetalert2";
import "./SocialLogin.css";

const GOOGLE_CLIENT_ID =
  //"936897005932-dtao9dg0t07sn13iiq1sjn331t6492g0.apps.googleusercontent.com";";
  "51956223448-f54kjnrckreear1kp237f4c4lu1nvok0.apps.googleusercontent.com";

const APPLE_CLIENT_ID = "com.mukemel.web.login"; // Apple Developer Console'dan alın
// Production için mukemel.com, development için localhost
const APPLE_REDIRECT_URI = window.location.hostname === "localhost" 
  ? "https://mukemel.com/userlogin"  // Apple localhost'u kabul etmiyor, popup modunda redirect kullanılmıyor
  : "https://mukemel.com/userlogin";

const SocialLogin = () => {
  const { t } = useTranslation();
  const googleTokenClientRef = useRef(null);
  const [isGoogleReady, setIsGoogleReady] = useState(false);
  const [isAppleReady, setIsAppleReady] = useState(false);
  const [isLoading, setIsLoading] = useState({
    google: false,
    apple: false,
  });

  // ========================
  // ORTAK FONKSİYONLAR (useCallback ile)
  // ========================
  const showError = useCallback(
    (message) => {
      Swal.fire({
        icon: "error",
        title: t("AlertErrorTitle"),
        text: message,
        confirmButtonText: t("OK"),
      });
    },
    [t]
  );

  const handleSocialLoginSuccess = useCallback(
    async (provider, userData) => {
      try {
        // Backend'e sosyal login isteği gönder
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/social-login`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              provider,
              ...userData,
            }),
          }
        );

        const data = await response.json();

        if (response.ok && data.user) {
          // Provider bilgisini user objesine ekle (backend'den gelmeyebilir)
          const userWithProvider = {
            ...data.user,
            provider: provider, // google veya apple
          };
          
          // Kullanıcı bilgilerini kaydet
          sessionStorage.setItem("user_id", data.user.id);
          localStorage.setItem("userId", data.user.id);
          localStorage.setItem("user", JSON.stringify(userWithProvider));

          Swal.fire({
            icon: "success",
            title: t("LoginSuccessful"),
            text: t("WelcomeUser", { name: data.user.name }),
            confirmButtonText: t("OK"),
          }).then(() => {
            window.location.href = `/profile/${data.user.id}`;
          });
        } else {
          showError(data.error || t("socialLogin.genericError"));
        }
      } catch (error) {
        console.error("Social login API error:", error);
        showError(t("APIerrortext"));
      }
    },
    [t, showError]
  );

  // ========================
  // GOOGLE LOGIN
  // ========================
  useEffect(() => {
    const initGoogle = () => {
      if (window.google?.accounts?.oauth2 && !googleTokenClientRef.current) {
        googleTokenClientRef.current =
          window.google.accounts.oauth2.initTokenClient({
            client_id: GOOGLE_CLIENT_ID,
            scope: "openid email profile",
            callback: async (tokenResponse) => {
              try {
                const accessToken = tokenResponse.access_token;

                // Google userinfo al
                const response = await fetch(
                  "https://www.googleapis.com/oauth2/v3/userinfo",
                  { headers: { Authorization: `Bearer ${accessToken}` } }
                );
                const profile = await response.json();

                // Backend'e gönder
                await handleSocialLoginSuccess("google", {
                  id: profile.sub,
                  email: profile.email,
                  name: profile.name,
                  picture: profile.picture,
                  accessToken,
                });
              } catch (error) {
                console.error("Google login error:", error);
                showError(t("socialLogin.googleError"));
              } finally {
                setIsLoading((prev) => ({ ...prev, google: false }));
              }
            },
          });
        setIsGoogleReady(true);
      }
    };

    const interval = setInterval(initGoogle, 200);
    initGoogle();
    return () => clearInterval(interval);
  }, [t, handleSocialLoginSuccess, showError]);

  const handleGoogleLogin = () => {
    if (!googleTokenClientRef.current) return;
    setIsLoading((prev) => ({ ...prev, google: true }));
    googleTokenClientRef.current.requestAccessToken({ prompt: "consent" });
  };

  // ========================
  // APPLE LOGIN
  // ========================
  useEffect(() => {
    // Apple JS SDK yükle
    if (!document.getElementById("apple-jssdk")) {
      const script = document.createElement("script");
      script.id = "apple-jssdk";
      script.src =
        "https://appleid.cdn-apple.com/appleauth/static/jsapi/appleid/1/en_US/appleid.auth.js";
      script.async = true;
      script.defer = true;
      script.onload = () => {
        if (window.AppleID) {
          window.AppleID.auth.init({
            clientId: APPLE_CLIENT_ID,
            scope: "name email",
            redirectURI: APPLE_REDIRECT_URI,
            usePopup: true,
          });
          setIsAppleReady(true);
        }
      };
      document.body.appendChild(script);
    } else if (window.AppleID) {
      setIsAppleReady(true);
    }
  }, []);

  const handleAppleLogin = async () => {
    if (!window.AppleID) {
      showError(t("socialLogin.appleNotReady"));
      return;
    }

    setIsLoading((prev) => ({ ...prev, apple: true }));

    try {
      const response = await window.AppleID.auth.signIn();

      // Apple response yapısı
      const { authorization, user } = response;

      // Apple id_token'dan email ve sub (user id) bilgisini çıkar
      // id_token bir JWT'dir, payload kısmını decode ediyoruz
      let emailFromToken = null;
      let subFromToken = null;

      if (authorization.id_token) {
        try {
          const tokenParts = authorization.id_token.split(".");
          if (tokenParts.length === 3) {
            const payload = JSON.parse(atob(tokenParts[1]));
            emailFromToken = payload.email;
            subFromToken = payload.sub; // Apple'ın benzersiz kullanıcı ID'si
          }
        } catch (e) {
          console.error("Token decode error:", e);
        }
      }

      // Email'i önce user objesinden, yoksa token'dan al
      const email = user?.email || emailFromToken;
      const appleUserId = subFromToken || authorization.code;
      const name = user?.name
        ? `${user.name.firstName || ""} ${user.name.lastName || ""}`.trim()
        : null;

      if (!email) {
        showError(
          t("socialLogin.appleEmailRequired") ||
            "Apple hesabınızdan email bilgisi alınamadı. Lütfen Apple ID ayarlarınızdan email paylaşımına izin verin."
        );
        return;
      }

      await handleSocialLoginSuccess("apple", {
        id: appleUserId,
        idToken: authorization.id_token,
        email: email,
        name: name,
        accessToken: authorization.code,
      });
    } catch (error) {
      console.error("Apple login error:", error);
      if (error.error !== "popup_closed_by_user") {
        showError(t("socialLogin.appleError"));
      }
    } finally {
      setIsLoading((prev) => ({ ...prev, apple: false }));
    }
  };

  return (
    <div className="social-login-container">
      <div className="social-login-divider">
        <span>{t("socialLogin.orContinueWith")}</span>
      </div>

      <div className="social-login-buttons">
        {/* Google Login Button */}
        <button
          type="button"
          className="social-login-btn google-btn"
          onClick={handleGoogleLogin}
          disabled={!isGoogleReady || isLoading.google}
        >
          <svg className="social-icon" viewBox="0 0 24 24">
            <path
              fill="#4285F4"
              d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
            />
            <path
              fill="#34A853"
              d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
            />
            <path
              fill="#FBBC05"
              d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
            />
            <path
              fill="#EA4335"
              d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
            />
          </svg>
          <span>
            {isLoading.google
              ? t("socialLogin.connecting")
              : t("socialLogin.google")}
          </span>
        </button>

        {/* Apple Login Button */}
        <button
          type="button"
          className="social-login-btn apple-btn"
          onClick={handleAppleLogin}
          disabled={!isAppleReady || isLoading.apple}
        >
          <svg className="social-icon" viewBox="0 0 24 24">
            <path
              fill="#000000"
              d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09l.01-.01zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z"
            />
          </svg>
          <span>
            {isLoading.apple
              ? t("socialLogin.connecting")
              : t("socialLogin.apple")}
          </span>
        </button>
      </div>
    </div>
  );
};

export default SocialLogin;
